import { useState } from 'react';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { Loading } from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import { PERIOD_OPTIONS, type StorePerfPeriod } from './storePerformanceModel';
import { StorePerformanceBand } from './StorePerformanceBand';
import { useStorePerformance } from './useStorePerformance';

/**
 * Store performance - the reports suite's first funnel surface and first real
 * chart (Reports > Store performance, /store-performance).
 *
 * It ranks stores by click and conversion volume per market and surfaces the
 * stores that pull real traffic but convert below par (the leak ops can fix).
 * One full-width band renders per market the scope derives to (UK one band, US
 * one band, GLOBAL two stacked), keyed off the region scope, not the switcher.
 *
 * It opens on LAST 30 DAYS ROLLING, not month to date: a funnel with a maturation
 * lag opens emptiest and least trustworthy on a to-date window, so rolling 30
 * gives a full mostly-matured window every time (D-store-performance).
 *
 * Read-only: no provider mutations, so the audit floor stays dormant. All counts
 * come through the store-performance seam; the surface never touches a raw click.
 */

/** The surface without the access wrapper. Exported so tests can inject `asOf`. */
export const StorePerformanceView = ({ asOf }: { asOf?: number }) => {
  // One reference clock for the period window and the maturation cutoff, captured
  // once via a lazy initializer (so the render body stays pure). Tests inject a
  // fixed value for determinism.
  const [now] = useState<number>(() => asOf ?? Date.now());
  const [period, setPeriod] = useState<StorePerfPeriod>('last30');

  const { view, isLoading } = useStorePerformance({ period, now });

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 1 }}
      >
        Reports
      </Typography>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Store performance
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Which stores pull traffic and convert it. The conversion rate is
        computed over matured clicks only, so a fresh store never reads as a
        leak.
      </Typography>

      {/* The period governs the volume columns and the chart; the rate column
          always applies the maturation cutoff on top of the selected window. */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
        <TextField
          select
          size="small"
          label="Period"
          value={period}
          onChange={(e) => setPeriod(e.target.value as StorePerfPeriod)}
          sx={{ minWidth: 220 }}
        >
          {PERIOD_OPTIONS.map((o) => (
            <MenuItem key={o.id} value={o.id}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {isLoading || !view ? (
        <Loading />
      ) : view.bands.length === 0 ? (
        <Typography color="text.secondary">
          No market is in scope for this account.
        </Typography>
      ) : (
        view.bands.map((band) => (
          <StorePerformanceBand key={band.market} band={band} />
        ))
      )}
    </Box>
  );
};

/**
 * Gated entry registered at /store-performance. `storePerformance:read` is a read
 * capability, so `viewer` (read everywhere) and `super_admin` see it; the
 * descriptive `editor` and the placeholder support / fraud roles do not, the same
 * reports-suite trust line as Earnings (D-store-performance).
 */
export const StorePerformance = () => (
  <RequireAccess capability="storePerformance:read">
    <StorePerformanceView />
  </RequireAccess>
);
